import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, CardBody, Input, Page, Table, useToast } from "@theg1group/ui";
import type { TableColumn } from "@theg1group/ui";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.ts";
import { formatAlertDetail } from "../lib/alertFormat.ts";

interface RepEntry {
	source: string;
	status: string;
	response: string | null;
}
interface WatchRow {
	name: string;
	wildcard: boolean | null;
	rdap_registrar: string | null;
	rdap_created: string | null;
	rdap_expires: string | null;
	active: boolean;
	note: string | null;
	last_checked_at: string | null;
	last_reputation_at: string | null;
	created_at: string | null;
	ip: string | null;
	asn: number | null;
	country: string | null;
	ip_count: number;
	ns_count: number;
	cert_count: number;
	open_alerts: number;
	reputation: RepEntry[];
	provider_tags: string[];
}

const SRC_SHORT: Record<string, string> = {
	spamhaus_sia: "SIA",
	spamhaus_dbl: "DBL",
	spamhaus_zen: "ZEN",
	google_safebrowsing: "GSB",
	urlhaus: "URLhaus",
	surbl: "SURBL",
	uribl: "URIBL",
};
interface AlertRow {
	id: string;
	target: string;
	kind: string;
	source: string | null;
	severity: string;
	detail: Record<string, unknown> | null;
	created_at: string;
	acknowledged_at: string | null;
}

function sevVariant(s: string): "error" | "warning" | "info" {
	if (s === "critical") return "error";
	if (s === "warn") return "warning";
	return "info";
}

interface SourceHealth {
	source: string;
	last_ok_at: string | null;
	last_error_at: string | null;
	last_status: string | null;
	consecutive_errors: number;
	checks_total: number;
	first_seen_at: string;
	updated_at: string;
}

function ago(ts: string | null): string {
	if (!ts) return "";
	const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
	if (mins < 60) return `${mins}m`;
	if (mins < 1440) return `${Math.round(mins / 60)}h`;
	return `${Math.round(mins / 1440)}d`;
}

/** One badge per reputation source, read from the single last_status field. */
function SourceBadge({ s }: { s: SourceHealth }) {
	if (s.last_status === "ok") return <Badge variant="success">{s.source} ✓ {ago(s.last_ok_at)}</Badge>;
	if (s.last_status === "no_key") return <Badge variant="default">{s.source} — no key</Badge>;
	// down = real outage. last_ok_at set → it worked before; null → never succeeded yet.
	return (
		<Badge variant="error">
			{s.source} ⚠ {s.last_ok_at ? `down ${ago(s.last_error_at)} (last ok ${ago(s.last_ok_at)})` : "down (no verdicts yet)"}
		</Badge>
	);
}

export function Monitor() {
	const qc = useQueryClient();
	const toast = useToast();
	const [domain, setDomain] = useState("");

	const watchlist = useQuery({
		queryKey: ["watchlist"],
		queryFn: () => api.get<{ rows: WatchRow[] }>("/api/monitor/watchlist"),
	});
	const alerts = useQuery({
		queryKey: ["alerts"],
		queryFn: () => api.get<{ rows: AlertRow[] }>("/api/monitor/alerts"),
	});
	const sources = useQuery({
		queryKey: ["sources"],
		queryFn: () => api.get<{ rows: SourceHealth[] }>("/api/monitor/sources"),
	});
	const refresh = () => {
		qc.invalidateQueries({ queryKey: ["watchlist"] });
		qc.invalidateQueries({ queryKey: ["alerts"] });
	};

	const addWatch = useMutation({
		mutationFn: (dom: string) => api.post("/api/monitor/watch", { domain: dom }),
		onSuccess: () => {
			toast.addToast("success", "Added to watchlist");
			setDomain("");
			qc.invalidateQueries({ queryKey: ["watchlist"] });
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	const unwatch = useMutation({
		mutationFn: (name: string) => api.del(`/api/monitor/watch/${encodeURIComponent(name)}`),
		onSuccess: () => {
			toast.addToast("success", "Removed");
			qc.invalidateQueries({ queryKey: ["watchlist"] });
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	const ack = useMutation({
		mutationFn: (id: string) => api.post(`/api/monitor/alerts/${id}/ack`),
		onSuccess: () => {
			toast.addToast("success", "Acknowledged");
			qc.invalidateQueries({ queryKey: ["alerts"] });
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const repCell = (r: WatchRow) => {
		const listed = r.reputation.filter((x) => x.status === "blacklisted" || x.status === "greylisted");
		if (listed.length) {
			return (
				<span className="flex flex-wrap gap-1">
					{listed.map((x) => (
						<Badge key={x.source} variant={x.status === "blacklisted" ? "error" : "warning"}>
							<span title={x.response ?? undefined}>{SRC_SHORT[x.source] ?? x.source}</span>
						</Badge>
					))}
				</span>
			);
		}
		if (r.reputation.length) return <Badge variant="success">clean</Badge>;
		return <span className="text-muted text-xs">unchecked</span>;
	};

	const columns: TableColumn<WatchRow>[] = [
		{
			id: "domain",
			header: "Domain",
			accessor: (r) => (
				<Link to={`/domains/${encodeURIComponent(r.name)}`} className="text-primary hover:underline font-mono text-xs">
					{r.name}
					{r.wildcard ? <span className="text-muted" title="wildcard zone"> *</span> : null}
				</Link>
			),
		},
		{ id: "rep", header: "Reputation", accessor: repCell },
		{
			id: "providers",
			header: "Providers",
			accessor: (r) =>
				r.provider_tags?.length ? (
					<span className="flex flex-wrap gap-1">
						{r.provider_tags.map((t) => (
							<Badge key={t} variant="info">
								{t}
							</Badge>
						))}
					</span>
				) : (
					<span className="text-muted text-xs">—</span>
				),
		},
		{ id: "registrar", header: "Registrar", accessor: (r) => <span className="text-xs">{r.rdap_registrar ?? "—"}</span> },
		{
			id: "ip",
			header: "IP · ASN",
			accessor: (r) =>
				r.ip ? (
					<span className="text-xs font-mono">
						{r.ip}
						{r.asn ? <span className="text-muted"> · AS{r.asn} {r.country ?? ""}</span> : null}
					</span>
				) : (
					"—"
				),
		},
		{ id: "certs", header: "Certs", accessor: (r) => (r.cert_count > 0 ? r.cert_count : "—") },
		{
			id: "expires",
			header: "Expires",
			accessor: (r) => {
				if (!r.rdap_expires) return "—";
				const days = Math.round((new Date(r.rdap_expires).getTime() - Date.now()) / 86400000);
				return (
					<span className={days < 30 ? "text-xs" : "text-xs text-muted"}>
						{days < 30 ? <Badge variant="warning">{days}d</Badge> : fmt(r.rdap_expires)}
					</span>
				);
			},
		},
		{
			id: "alerts",
			header: "Alerts",
			accessor: (r) => (r.open_alerts > 0 ? <Badge variant="warning">{r.open_alerts}</Badge> : "—"),
		},
		{ id: "added", header: "Added", accessor: (r) => <span className="text-xs text-muted">{fmt(r.created_at)}</span> },
		{ id: "checked", header: "Checked", accessor: (r) => <span className="text-xs text-muted">{fmt(r.last_checked_at)}</span> },
		{
			id: "rep_checked",
			header: "Rep ✓",
			accessor: (r) => (
				<span className="text-xs text-muted" title="reputation last verified (30-min sweep)">
					{r.last_reputation_at ? new Date(r.last_reputation_at).toLocaleString() : "—"}
				</span>
			),
		},
		{
			id: "act",
			header: "",
			accessor: (r) => (
				<button type="button" className="text-xs text-muted hover:text-content" onClick={() => unwatch.mutate(r.name)}>
					unwatch
				</button>
			),
		},
	];

	return (
		<Page title="Monitor" description="Watchlisted domains, blacklist reputation, and infrastructure-change alerts.">
			{sources.data?.rows.length ? (
				<Card className="mb-3.5">
					<CardBody>
						<div className="flex items-center gap-2 flex-wrap">
							<h2 className="text-sm font-semibold text-content mr-1">Sources</h2>
							{sources.data.rows.map((s) => (
								<SourceBadge key={s.source} s={s} />
							))}
						</div>
					</CardBody>
				</Card>
			) : null}
			<Card className="mb-3.5">
				<CardBody>
					<div className="flex items-center justify-between mb-2.5">
						<h2 className="text-sm font-semibold text-content">Watchlist</h2>
						<form
							className="flex gap-2"
							onSubmit={(e) => {
								e.preventDefault();
								const dom = domain.trim().toLowerCase();
								if (dom) addWatch.mutate(dom);
							}}
						>
							<Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="add domain…" />
							<Button type="submit" variant="secondary" loading={addWatch.isPending}>
								Watch
							</Button>
						</form>
					</div>
					<div className="overflow-x-auto">
						<Table
							columns={columns}
							data={watchlist.data?.rows ?? []}
							keyExtractor={(r) => r.name}
							loading={watchlist.isLoading}
							emptyMessage="No domains watched yet — add one, or hit Watch on a domain page."
						/>
					</div>
				</CardBody>
			</Card>

			<Card>
				<CardBody>
					<div className="flex items-center justify-between mb-2.5">
						<h2 className="text-sm font-semibold text-content">Alerts</h2>
						<button type="button" className="text-xs text-muted hover:text-content" onClick={refresh}>
							refresh
						</button>
					</div>
					{alerts.data?.rows.length ? (
						<div className="flex flex-col gap-1.5">
							{alerts.data.rows.map((a) => (
								<div key={a.id} className="text-sm border-b border-line/40 pb-1.5 last:border-0">
									<div className="flex items-center gap-2">
										<Badge variant={sevVariant(a.severity)}>{a.kind}</Badge>
										{a.kind === "source_down" ? (
											<span className="font-mono text-xs text-content">{a.target}</span>
										) : (
											<Link to={`/domains/${encodeURIComponent(a.target)}`} className="text-primary hover:underline font-mono text-xs">
												{a.target}
											</Link>
										)}
										{a.source ? <span className="text-muted text-xs">{a.source}</span> : null}
										<span className="text-muted text-[11px]">{new Date(a.created_at).toLocaleString()}</span>
										<button type="button" className="ml-auto text-xs text-muted hover:text-content" onClick={() => ack.mutate(a.id)}>
											ack
										</button>
									</div>
									{formatAlertDetail(a.kind, a.detail) ? (
										<div className="text-muted text-xs font-mono break-all mt-0.5 pl-1">{formatAlertDetail(a.kind, a.detail)}</div>
									) : null}
								</div>
							))}
						</div>
					) : (
						<div className="text-xs text-muted">No open alerts.</div>
					)}
				</CardBody>
			</Card>
		</Page>
	);
}

function fmt(v: string | null): string {
	return v ? new Date(v).toLocaleDateString() : "—";
}
